import { useEffect, useState } from "react";
import { ChevronLeft, Loader2, ChevronDown, ChevronUp, FileText, X, MapPin, Truck, Info, HelpCircle, Search, Clipboard, AlertCircle } from "lucide-react";
import toast from "react-hot-toast";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";
import PrescriptionList from "./PrescriptionList";
import { useAuth } from "../../context/AuthContext";
import { LabService } from "../../services/api/LaborderService/lab.service";
import { OnlineOrderService } from "../../services/api/OrderService/OnlineOrder";
import { Driver } from "../../services/api/LaborderService/types";
import CancelOrderModal from "./CancelOrderModal";
import AddressSelectionModal from "../common/AddressModal/AddressSelectionModal";

interface ViewOrderDetailsProps {
  order: { orderId: number; orderNumber: string };
  onBack: () => void;
  hidePrescriptions?: boolean;
}

const ORDER_STATUS_STEPS = [
  { status: "pending", label: "Payment Pending" },
  { status: "clinic_assigned", label: "Clinic Assigned" },
  { status: "admin_approval_pending", label: "Admin Review" },
  { status: "patient_approval_pending", label: "Patient Review" },
  { status: "substitute_generated", label: "Substitute Generated" },
  { status: "delivery_assigned", label: "Delivery Assigned" },
  { status: "out_for_delivery", label: "Out For Delivery" },
  { status: "delivered", label: "Delivered" },
];

const ViewOrderDetails: React.FC<ViewOrderDetailsProps> = ({ order, onBack, hidePrescriptions = false }) => {
  const [orderDetails, setOrderDetails] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPrescriptions, setShowPrescriptions] = useState(false);
  const [showBill, setShowBill] = useState(false);
  const [pdfBillUrl, setPdfBillUrl] = useState<string | null>(null);
  const [activeHoverId, setActiveHoverId] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);

  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";
  const [reassigning, setReassigning] = useState(false);
  const [deliveryPersons, setDeliveryPersons] = useState<Driver[]>([]);
  const [showRiderModal, setShowRiderModal] = useState(false);
  const [riderSearch, setRiderSearch] = useState("");

  const fetchAvailableDriver = async () => {
    if (!clinicId) return;
    try {
      const response = await LabService.getAvailableDrivers(clinicId, "driver");
      if (response.success) {
        setDeliveryPersons(response.data);
      }
    } catch (error) {
      console.error("Error fetching drivers:", error);
    }
  };

  useEffect(() => {
    fetchAvailableDriver();
  }, [clinicId]);

  const handleSelectDriver = async (driver: Driver) => {
    try {
      setReassigning(true);
      const response = await OnlineOrderService.reassignDriver(order.orderId, driver.id);
      if (response.success) {
        toast.success(`Rider ${driver.name} reassigned successfully!`);
        setShowRiderModal(false);
        fetchOrderDetails();
      } else {
        toast.error(response.message || "Failed to reassign driver");
      }
    } catch (error) {
      toast.error(error as string);
    } finally {
      setReassigning(false);
    }
  };

  const filteredDrivers = deliveryPersons.filter((driver) =>
    driver.name.toLowerCase().includes(riderSearch.toLowerCase())
  );

  useEffect(() => {
    fetchOrderDetails();
  }, [order.orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await AdminOrderManagementService.getOrderDetails(order.orderId);
      setOrderDetails(response);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={32} className="animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="p-5">
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer mb-4 hover:text-slate-700">
          <ChevronLeft size={16} /> Back to Orders
        </button>
        <p className="text-sm text-red-500">Failed to load order details.</p>
      </div>
    );
  }

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getStatusLabel = (status: string) => {
    if (!status) return "—";
    return status.replace(/_/g, " ").toUpperCase();
  };

  const getStatusStyle = (status: string) => {
    if (!status) return "text-slate-500 bg-slate-50 border border-slate-200";
    const clean = status.toLowerCase();
    if (clean === "pending") return "text-amber-600 bg-amber-50 border border-amber-200";
    if (clean === "cancelled") return "text-rose-600 bg-rose-50 border border-rose-200";
    if (clean === "delivered") return "text-emerald-600 bg-emerald-50 border border-emerald-200";
    if (clean === "out_for_delivery") return "text-blue-600 bg-blue-50 border border-blue-200";
    return "text-indigo-600 bg-indigo-50 border border-indigo-200";
  };

  const currentStatus = orderDetails.orderStatus?.toLowerCase() || "pending";
  const isStuckAtPayment = currentStatus === "pending";
  return (
    <div className="h-full bg-slate-50 flex flex-col overflow-hidden relative">
      <div className="flex-1 overflow-auto p-5">
        <div className="flex items-center justify-between mb-4">
          <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer hover:text-slate-700">
            <ChevronLeft size={16} /> Back to Orders
          </button>
          {currentStatus !== "cancelled" && currentStatus !== "delivered" && (
            <button
              onClick={() => setShowCancelModal(true)}
              className="px-3.5 py-1.5 rounded-lg text-xs font-bold text-rose-600 bg-rose-50 hover:bg-rose-100 border border-rose-200 transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm"
            >
              <AlertCircle size={14} /> Cancel Order
            </button>
          )}
        </div>

        {/* 1. Stuck at Payment Pending Alert */}
        {isStuckAtPayment && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3 shadow-sm animate-fadeIn">
            <Info className="text-amber-500 w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-xs font-extrabold text-amber-800 uppercase tracking-wide">Payment Stuck / Pending</h4>
              <p className="text-[11px] text-amber-700 font-semibold mt-1">
                This order is currently stuck at payment pending. The patient has to complete payment to proceed with execution.
              </p>
            </div>
          </div>
        )}

        <div className="flex gap-5">
          {/* Left Column: Details & Products */}
          <div className="flex-1 min-w-0 flex flex-col gap-4">

            {/* 3. Patient Details Card */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-slate-100">
                <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Patient Details</h3>
                <span className={`px-2 py-1 rounded-lg font-bold text-[0.62rem] tracking-wide inline-block ${getStatusStyle(orderDetails.orderStatus)}`}>
                  {getStatusLabel(orderDetails.orderStatus)}
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-semibold text-slate-600">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5">Invoice Number</span>
                  <span className="text-slate-800 font-extrabold text-sm">#{orderDetails.orderNumber}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5">Order Date</span>
                  <span className="text-slate-800 font-bold">{formatDate(orderDetails.orderDate)}</span>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5">Ordered By</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    {orderDetails.patientImage && (
                      <img src={orderDetails.patientImage} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-200" />
                    )}
                    <div>
                      <div className="text-slate-800 font-bold">{orderDetails.patientName}</div>
                      <div className="text-[0.6rem] text-slate-400 font-medium">+91 {orderDetails.patientPhone}</div>
                    </div>
                  </div>
                </div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5">Ordered For</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    {orderDetails.orderForImage && (
                      <img src={orderDetails.orderForImage} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-200" />
                    )}
                    <div>
                      <div className="text-slate-800 font-bold">{orderDetails.orderedForName || orderDetails.patientName}</div>
                      <div className="text-[0.6rem] text-slate-400 font-medium">+91 {orderDetails.orderedForPhone || orderDetails.patientPhone}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Clinic Name & Phone */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs font-semibold text-slate-600 mt-4 pt-3 border-t border-slate-50">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide block mb-0.5">Clinic Details</span>
                  <span className="text-slate-800 font-bold">{orderDetails.clinicName || "—"}</span>
                  {orderDetails.clinicPhone && (
                    <span className="text-[0.6rem] text-slate-400 block font-medium">+91 {orderDetails.clinicPhone}</span>
                  )}
                </div>
                <div className="col-span-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-slate-400 uppercase tracking-wide">Delivery Address</span>
                    <button
                      onClick={() => setShowAddressModal(true)}
                      className="text-[0.6rem] text-emerald-600 hover:text-white bg-emerald-50 hover:bg-emerald-500 rounded px-1.5 py-0.5 border border-emerald-200 font-bold cursor-pointer transition-all shadow-xs"
                    >
                      Edit Address
                    </button>
                  </div>
                  <div className="text-xs text-slate-700 flex items-start gap-1">
                    <MapPin size={12} className="text-rose-500 mt-0.5 flex-shrink-0" />
                    <span>
                      {orderDetails.address}, {orderDetails.city}, {orderDetails.state} - {orderDetails.pincode}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 4. Driver Assignment Card (If assigned) */}
            {(orderDetails.deliveryName || orderDetails.deliveryPhone) && (
              <div className="bg-emerald-50/20 border border-emerald-100 rounded-xl p-4 flex items-center justify-between shadow-sm animate-fadeIn">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700">
                    <Truck size={20} />
                  </div>
                  <div>
                    <h4 className="text-xs font-extrabold text-emerald-800 uppercase tracking-wide">Delivery Rider Details</h4>
                    <p className="text-xs text-slate-700 font-semibold mt-0.5">{orderDetails.deliveryName || "Delivery Driver"}</p>
                    {orderDetails.deliveryPhone && (
                      <p className="text-[10px] text-slate-400 font-medium">+91 {orderDetails.deliveryPhone}</p>
                    )}
                  </div>
                </div>
                {clinicId && 
                 orderDetails.orderStatus?.toLowerCase() !== "delivered" && 
                 orderDetails.orderStatus?.toLowerCase() !== "cancelled" && (
                   <button
                     onClick={() => {
                       setRiderSearch("");
                       setShowRiderModal(true);
                     }}
                     className="px-3.5 py-1.5 text-[10px] font-bold bg-[#e6f7f4] hover:bg-[#d1f2ec] text-[#00a896] border border-[#00a896] rounded-lg transition-all cursor-pointer shadow-sm"
                   >
                     Reassign Rider
                   </button>
                )}
              </div>
            )}

            {/* 5. Collapsible Prescriptions */}
            {!hidePrescriptions && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button
                  className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                  onClick={() => setShowPrescriptions(!showPrescriptions)}
                >
                  <span className="uppercase tracking-wider">Uploaded Prescriptions</span>
                  {showPrescriptions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showPrescriptions && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                    <PrescriptionList
                      prescriptions={orderDetails.prescriptions}
                      orderId={orderDetails.orderId}
                      orderDetails={orderDetails}
                      onUploadSuccess={fetchOrderDetails}
                    />
                  </div>
                )}
              </div>
            )}

            {/* 6. Collapsible Bill (If Generated) */}
            {orderDetails.bill && (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
                <button
                  className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                  onClick={() => setShowBill(!showBill)}
                >
                  <span className="uppercase tracking-wider">Order Invoice / Bill</span>
                  {showBill ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                {showBill && (
                  <div className="px-5 pb-5 border-t border-slate-100 pt-4 flex items-center justify-between bg-slate-50/50">
                    <div className="flex items-center gap-2">
                      <FileText className="text-rose-500 w-8 h-8" />
                      <div>
                        <p className="text-xs font-bold text-slate-800">Invoice Bill PDF</p>
                        <p className="text-[10px] text-slate-400">Official tax receipt generated by Clinic</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setPdfBillUrl(orderDetails.bill)}
                      className="px-4 py-2 rounded-lg text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm transition-all cursor-pointer border-none"
                    >
                      👁️ Preview Bill
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* 7. Products Table Layout (Read-only, hovering substitute displays popup) */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-4 border-b border-slate-100 pb-2">Ordered & Replacement Products</h3>
              <div className="grid grid-cols-[3fr_1.2fr_1.2fr_1.2fr_1fr] gap-2 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-50 pb-2 mb-2">
                <div>Product Name</div>
                <div>MRP</div>
                <div>Price (Final)</div>
                <div className="text-center">Quantity</div>
                <div className="text-center">Total</div>
              </div>
              <div className="space-y-1">
                {orderDetails.products?.map((product: any, idx: number) => {
                  const isReplaced = !!product.substituteProductId && product.substituteStatus === "Approved";
                  const displayName = isReplaced ? product.substituteProductName : product.name;
                  const displayImage = isReplaced ? product.substituteProductImage : product.productImage;
                  const displayMrp = isReplaced ? product.substituteMrp : product.productMrp;
                  const displayPrice = isReplaced ? product.substitutePrice : product.productPrice;

                  const mrpNum = parseFloat(displayMrp) || 0;
                  const priceNum = parseFloat(displayPrice) || 0;
                  const finalTotal = priceNum * product.quantity;

                  const originalMrpNum = parseFloat(product.productMrp) || 0;
                  const originalPriceNum = parseFloat(product.productPrice) || 0;

                  const isRejected = !!product.substituteProductId && product.substituteStatus === "Rejected";

                  const uniqueId = `${product.productId}-${idx}`;

                  return (
                    <div
                      key={uniqueId}
                      className={`grid grid-cols-[3fr_1.2fr_1.2fr_1.2fr_1fr] gap-2 items-center py-2.5 px-2 rounded-lg border border-slate-50 hover:bg-slate-50/50 transition-colors relative ${isReplaced ? "bg-emerald-50/5 border-emerald-50/50" : isRejected ? "bg-rose-50/5 border-rose-50/50" : ""
                        }`}
                    >
                      {/* Product Name */}
                      <div className="flex items-center gap-2.5 min-w-0 pr-2 relative">
                        {displayImage && (
                          <img src={displayImage} alt="" className="w-8 h-8 object-cover rounded-md border border-slate-100 flex-shrink-0"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                        )}
                        <div className="min-w-0 relative">
                          <div className="text-xs font-bold text-slate-700 truncate" title={displayName}>
                            {displayName}
                          </div>

                          {/* Replaced Indicators & Hover Popup Trigger */}
                          {isReplaced ? (
                            <div
                              className="relative inline-block mt-0.5"
                              onMouseEnter={() => setActiveHoverId(uniqueId)}
                              onMouseLeave={() => setActiveHoverId(null)}
                            >
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-extrabold bg-emerald-50 text-emerald-600 border border-emerald-100 uppercase cursor-pointer hover:bg-emerald-100 transition-colors">
                                <HelpCircle size={10} className="stroke-[2.5]" /> Replaced Product
                              </span>

                              {/* Replaced Hover Comparison Card Popup */}
                              {activeHoverId === uniqueId && (
                                <div className="absolute left-0 bottom-full mb-2 bg-white border border-slate-200 rounded-xl shadow-2xl p-4 min-w-[280px] max-w-[340px] z-[250000] text-slate-700 text-xs font-semibold animate-fadeUp">
                                  <div className="flex items-center gap-1 pb-1.5 border-b border-slate-100 mb-2">
                                    <span className="px-1.5 py-0.5 rounded text-[8px] font-extrabold bg-indigo-50 text-indigo-600 border border-indigo-100 uppercase">Original</span>
                                    <span className="text-[10px] font-extrabold text-slate-400">vs</span>
                                    <span className="px-1.5 py-0.5 rounded text-[8px] font-extrabold bg-emerald-50 text-emerald-600 border border-emerald-100 uppercase">Replacement</span>
                                  </div>

                                  <div className="space-y-2">
                                    {/* Original */}
                                    <div className="flex flex-col bg-slate-50 p-2 rounded-lg border border-slate-100">
                                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Original Item</p>
                                      <p className="text-slate-700 font-bold text-xs truncate">{product.name}</p>
                                      <p className="text-[10px] text-slate-500 font-semibold mt-0.5">MRP: ₹{originalMrpNum.toFixed(2)} • Price: ₹{originalPriceNum.toFixed(2)}</p>
                                    </div>
                                    {/* Replacement */}
                                    <div className="flex flex-col bg-emerald-50/20 p-2 rounded-lg border border-emerald-100/50">
                                      <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider mb-0.5">Replacement Item</p>
                                      <p className="text-slate-800 font-bold text-xs truncate">{product.substituteProductName}</p>
                                      <p className="text-[10px] text-emerald-700 font-bold mt-0.5">MRP: ₹{mrpNum.toFixed(2)} • Price: ₹{priceNum.toFixed(2)}</p>
                                    </div>
                                  </div>

                                  {/* Note */}
                                  {product.substituteNote && (
                                    <div className="mt-2.5 pt-2 border-t border-slate-100">
                                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Replacement Note</p>
                                      <p className="text-[10px] text-slate-600 italic bg-amber-50/40 p-1.5 rounded border border-amber-100/50 font-medium">
                                        "{product.substituteNote}"
                                      </p>
                                    </div>
                                  )}

                                  {/* Tooltip Arrow */}
                                  <div className="absolute top-full left-5 border-[6px] border-transparent border-t-white z-[250001]" />
                                  <div className="absolute top-full left-5 border-[7px] border-transparent border-t-slate-200 z-[249999] -mt-[1px]" />
                                </div>
                              )}
                            </div>
                          ) : isRejected ? (
                            <div
                              className="relative inline-block mt-0.5"
                              onMouseEnter={() => setActiveHoverId(uniqueId)}
                              onMouseLeave={() => setActiveHoverId(null)}
                            >
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-extrabold bg-rose-50 text-rose-600 border border-rose-100 uppercase cursor-pointer hover:bg-rose-100 transition-colors">
                                <HelpCircle size={10} className="stroke-[2.5]" /> Substitute Rejected
                              </span>

                              {/* Rejected Substitute Hover Popup */}
                              {activeHoverId === uniqueId && (
                                <div className="absolute left-0 bottom-full mb-2 bg-white border border-slate-200 rounded-xl shadow-2xl p-4 min-w-[280px] max-w-[340px] z-[250000] text-slate-700 text-xs font-semibold animate-fadeUp">
                                  <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-100 mb-2">
                                    <span className="px-1.5 py-0.5 rounded text-[8px] font-extrabold bg-rose-50 text-rose-600 border border-rose-100 uppercase">Rejected Substitute</span>
                                  </div>

                                  <div className="space-y-2">
                                    {/* Current (Original) Product */}
                                    <div className="flex flex-col bg-emerald-50/30 p-2 rounded-lg border border-emerald-100/50">
                                      <p className="text-[10px] text-emerald-600 font-bold uppercase tracking-wider mb-0.5">Keeping Original</p>
                                      <p className="text-slate-800 font-bold text-xs truncate">{product.name}</p>
                                      <p className="text-[10px] text-slate-500 font-semibold mt-0.5">MRP: ₹{originalMrpNum.toFixed(2)} • Price: ₹{originalPriceNum.toFixed(2)}</p>
                                    </div>
                                    {/* Rejected Substitute */}
                                    <div className="flex flex-col bg-rose-50/30 p-2 rounded-lg border border-rose-100/50">
                                      <p className="text-[10px] text-rose-500 font-bold uppercase tracking-wider mb-0.5">Rejected Substitute</p>
                                      <p className="text-slate-600 font-bold text-xs truncate line-through">{product.substituteProductName}</p>
                                      <p className="text-[10px] text-slate-400 font-semibold mt-0.5">MRP: ₹{parseFloat(product.substituteMrp || "0").toFixed(2)} • Price: ₹{parseFloat(product.substitutePrice || "0").toFixed(2)}</p>
                                    </div>
                                  </div>

                                  {/* Rejection Note */}
                                  {product.substituteNote && (
                                    <div className="mt-2.5 pt-2 border-t border-slate-100">
                                      <p className="text-[9px] text-slate-400 font-bold uppercase tracking-wider mb-0.5">Rejection Note</p>
                                      <p className="text-[10px] text-slate-600 italic bg-rose-50/40 p-1.5 rounded border border-rose-100/50 font-medium">
                                        "{product.substituteNote}"
                                      </p>
                                    </div>
                                  )}

                                  {/* Tooltip Arrow */}
                                  <div className="absolute top-full left-5 border-[6px] border-transparent border-t-white z-[250001]" />
                                  <div className="absolute top-full left-5 border-[7px] border-transparent border-t-slate-200 z-[249999] -mt-[1px]" />
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-extrabold bg-blue-50 text-blue-600 border border-blue-100 uppercase mt-0.5">
                              Standard
                            </span>
                          )}
                        </div>
                      </div>

                      {/* MRP */}
                      <div className="text-[11px] text-slate-500 font-semibold whitespace-nowrap">
                        ₹ {mrpNum.toFixed(2)}
                      </div>

                      {/* Price */}
                      <div className="text-[11px] text-slate-800 font-bold whitespace-nowrap">
                        ₹ {priceNum.toFixed(2)}
                      </div>

                      {/* Quantity */}
                      <div className="text-center text-[11px] text-slate-700 font-extrabold whitespace-nowrap">
                        {product.quantity}
                      </div>

                      {/* Total */}
                      <div className="text-center text-[11px] text-slate-800 font-extrabold whitespace-nowrap">
                        ₹ {finalTotal.toFixed(2)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right Column: Payment Summary Sidebar */}
          <div className="w-[320px] flex-shrink-0 hidden lg:block">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col gap-4 sticky top-5">
              {/* Order Note (if present) */}
              {(orderDetails?.ordernote || orderDetails?.orderNote) && (
                <div className="p-3 bg-amber-50/80 border border-amber-200 rounded-xl">
                  <div className="flex items-center gap-1.5 mb-1 text-amber-700">
                    <Clipboard size={14} className="flex-shrink-0" />
                    <span className="text-[11px] font-bold uppercase tracking-wider">Order Note</span>
                  </div>
                  <p className="text-xs text-amber-900 leading-relaxed font-medium">
                    {orderDetails?.ordernote || orderDetails?.orderNote}
                  </p>
                </div>
              )}

              <h4 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">Payment Summary</h4>

              {/* Breakdowns */}
              <div className="flex flex-col gap-3.5 text-xs font-semibold text-slate-500">
                <div className="flex justify-between">
                  <span>MRP Total</span>
                  <span className="text-slate-800 font-bold">₹{orderDetails.paymentSummary?.mrpTotal?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between text-emerald-600 bg-emerald-50/50 px-2 py-1.5 rounded-lg border border-emerald-100/50">
                  <span>Discount Applied</span>
                  <span className="font-extrabold">-₹{orderDetails.paymentSummary?.discount?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between">
                  <span>Consultation Fee</span>
                  <span className="text-slate-800 font-bold">₹{orderDetails.paymentSummary?.medicalConsultationFee?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between">
                  <span>Total GST</span>
                  <span className="text-slate-800 font-bold">₹{orderDetails.paymentSummary?.totalGst?.toFixed(2) || "0.00"}</span>
                </div>
                {orderDetails.paymentSummary?.coinsUsed > 0 && (
                  <div className="flex justify-between">
                    <span>Coins Used</span>
                    <span className="text-slate-800 font-bold">-{orderDetails.paymentSummary.coinsUsed}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Taxes & Charges</span>
                  <span className="text-slate-800 font-bold">₹{orderDetails.paymentSummary?.taxAndCharges?.toFixed(2) || "0.00"}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery Fee</span>
                  <span className="text-slate-800 font-bold">₹{orderDetails.paymentSummary?.deliveryFee?.toFixed(2) || "0.00"}</span>
                </div>
                {orderDetails.paymentSummary?.packagingCharges > 0 && (
                  <div className="flex justify-between">
                    <span>Packaging Charges</span>
                    <span className="text-slate-800 font-bold">₹{orderDetails.paymentSummary.packagingCharges.toFixed(2)}</span>
                  </div>
                )}

                <hr className="border-slate-100 my-0.5" />

                <div className="flex justify-between items-center text-sm font-extrabold text-slate-800 bg-slate-50 p-3 rounded-xl border border-slate-100">
                  <span>Amount Paid</span>
                  <span className="text-lg font-black text-emerald-600">₹{orderDetails.paymentSummary?.paymentAmount || "0"}</span>
                </div>

                {/* Additional Payment Info */}
                <div className="mt-2 bg-slate-50/50 rounded-xl border border-slate-100 p-3 flex flex-col gap-2 text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                  <div className="flex justify-between">
                    <span>Method</span>
                    <span className="text-slate-600 font-extrabold">{orderDetails.paymentSummary?.paymentMethod || "online"}</span>
                  </div>
                  {orderDetails.paymentSummary?.paymentDate && (
                    <div className="flex justify-between">
                      <span>Date</span>
                      <span className="text-slate-600 font-extrabold">
                        {new Date(orderDetails.paymentSummary.paymentDate).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bill Preview PDF Modal */}
      {pdfBillUrl && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[300000] flex items-center justify-center p-4 animate-fadeIn" onClick={() => setPdfBillUrl(null)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-slideUp" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
              <h3 className="text-sm font-bold text-slate-800">Order Invoice / Bill</h3>
              <button
                className="w-8 h-8 rounded-lg bg-slate-105 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
                onClick={() => setPdfBillUrl(null)}
              >
                <X size={16} />
              </button>
            </div>
            {/* Body */}
            <div className="flex-1 bg-slate-100">
              <iframe
                src={`${pdfBillUrl}#toolbar=0`}
                className="w-full h-full border-none"
                title="Invoice Bill PDF"
              />
            </div>
          </div>
        </div>
      )}

      {/* RIDER SELECT MODAL */}
      {showRiderModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200000] flex items-center justify-center animate-fadeIn" onClick={() => setShowRiderModal(false)}>
          <div className="bg-white rounded-2xl max-w-[480px] w-[95%] max-h-[80vh] flex flex-col shadow-2xl overflow-hidden animate-slideUp" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-bold text-slate-800">Select Available Rider</h3>
              <button
                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
                onClick={() => setShowRiderModal(false)}
              >
                <X size={16} />
              </button>
            </div>

            {/* Rider Search */}
            <div className="px-5 py-3 border-b border-slate-100">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search available riders by name..."
                  value={riderSearch}
                  onChange={(e) => setRiderSearch(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-xs outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Riders List */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-2">
              {reassigning ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-emerald-500" size={24} />
                </div>
              ) : filteredDrivers.length === 0 ? (
                <div className="text-center py-6 text-xs text-slate-400 italic">No riders found.</div>
              ) : (
                filteredDrivers.map((driver) => {
                  const isCurrent = driver.name === orderDetails.deliveryName;
                  return (
                    <div
                      key={driver.id}
                      onClick={() => handleSelectDriver(driver)}
                      className={`flex items-center justify-between p-3 rounded-xl border transition-all cursor-pointer group ${isCurrent
                        ? "border-indigo-500 bg-indigo-50/40"
                        : "border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/10"
                        }`}
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={driver.profilePicture || "https://via.placeholder.com/40"}
                          alt=""
                          className="w-10 h-10 rounded-full object-cover border border-slate-200 bg-white"
                        />
                        <div>
                          <div className="text-xs font-bold text-slate-800">{driver.name}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">📞 {driver.mobileNumber}</div>
                        </div>
                      </div>
                      <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-[10px] font-bold text-slate-500 border border-slate-200/50">
                        {driver.pendingOrdersCount} ongoing
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
      <CancelOrderModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        orderId={order.orderId}
        orderNumber={order.orderNumber}
        onSuccess={onBack}
      />
      {orderDetails && (
        <AddressSelectionModal
          isOpen={showAddressModal}
          onClose={() => setShowAddressModal(false)}
          patientId={orderDetails.patientId}
          orderId={order.orderId}
          currentAddress={orderDetails.address}
          currentPincode={orderDetails.pincode}
          currentClinicId={orderDetails.clinicId}
          onAddressUpdated={fetchOrderDetails}
        />
      )}
    </div>
  );
};

export default ViewOrderDetails;
